import { useEffect, useState } from 'react';
import { fetchExperimentalFeatureUsageStats } from '../services/dashboard.service';
import { useFilterContext } from '../usageandbilling/FilterContext';

export interface ExperimentalFeatureAdoption {
  featureId: string;
  usersEnabled: number;
  orgsCount: number;
  pctOfActiveUsers: number;
}

export interface ExperimentalFeatureUsageStats {
  totalUsers: number;
  featureBreakdown: ExperimentalFeatureAdoption[];
}

/**
 * Snapshot-only: experimental flags carry no toggle-history, so this reflects
 * current adoption, not a trend — it only refetches on the org/user filter,
 * never on date range (there's no dated data to filter by).
 */
export function useExperimentalFeatureUsage() {
  const { selectedOrgIds, selectedUserIds } = useFilterContext();
  const [data, setData] = useState<ExperimentalFeatureUsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (selectedOrgIds.length > 0) params.set('orgIds', selectedOrgIds.join(','));
        if (selectedUserIds.length > 0) params.set('userIds', selectedUserIds.join(','));

        const response = await fetchExperimentalFeatureUsageStats(params.toString(), controller.signal);
        setData(response as ExperimentalFeatureUsageStats);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setError('Failed to load experimental feature usage.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    return () => controller.abort();
  }, [selectedOrgIds, selectedUserIds]);

  return { data, loading, error };
}
